/**
 * ObstacleModel: renders Synth Riders obstacle FBX models in the 3D tunnel.
 * Per-obstacle config: shape, scaleX/Y, defaultX/Y, rotation.
 * Global color settings: fillColor, fillOpacity, fresnelColor, fresnelOpacity, fresnelIntensity.
 *
 * PERFORMANCE MODEL (matches the note renderer's Stage-A overhaul):
 *  - The fresnel rim is FOLDED into the fill material via onBeforeCompile (one
 *    mesh per FBX geometry instead of two, halving transparent draws).
 *  - Materials are SHARED: exactly two per settings snapshot (normal + dimmed),
 *    cached at module level — obstacles never create materials, they swap
 *    between the two when crossing the trigger.
 *  - There is NO per-obstacle useFrame. Every mounted obstacle registers into a
 *    module registry and ONE `tickObstacles()` call (from the windowed renderer)
 *    updates z-position, grid-pulse and dim state for all of them.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useFBX, useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useEditorStore } from '../../store/editorStore';
import type { ObstacleConfigKey } from '../../store/editorStore';
import { OBSTACLE_SHAPE_FBX, DEFAULT_OBSTACLE_CONFIGS } from '../../store/editorStore';
import type { ObstacleShape } from '../../store/editorSettings';
import { isSynthVrView, selectToolAvailable } from '../../store/editorSettings';
import type { Slide, Obstacle } from '../../types/beatmap';
import { WALL_COLORS, parseWallColor } from '../../types/beatmap';
import { BEAT_SPACING, chartBeat, VISIBLE_BEATS_BEHIND } from './TunnelScene';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { wallXY, desktopWallDefaults, crouchWallEntries } from './wallGeometry';
import { startObjectDrag, dropPickBounds, NO_RAYCAST, dimPoolRaycast, isAltRightPress, isCtrlRightPress, jumpPlayheadToObject } from './objectDrag';
import { PERF, RENDER_STATS } from '../../perf/adaptiveBudget';
import { HaloPool, onBeatTolerance, haloColor, haloOnGridNow, makeHaloMaterial, makeHaloMesh, wallHaloGeometry, wallRimGeometry, WALL_RIM_MATERIAL } from './halo';

// ── Constants (must match TunnelScene.tsx) ────────────────────────────────────

const NOTE_Y_OFFSET = 1.8;
const TUNNEL_WIDTH = 5;

// ── Preload all FBX/texture paths at module scope ────────────────────────────

const allShapeDefs = Object.values(OBSTACLE_SHAPE_FBX);
const uniqueFbxPaths = new Set(allShapeDefs.map(d => d.fbx));
const uniqueTexPaths = new Set(allShapeDefs.map(d => d.texture));

uniqueFbxPaths.forEach(path => useFBX.preload(path));
uniqueTexPaths.forEach(path => useTexture.preload(path));

// ── Shared obstacle materials (fill + folded-in fresnel rim) ─────────────────
// Two materials per settings snapshot: normal and dimmed (behind the trigger).
// Cached by a settings key so ALL obstacles share the same two instances and
// dimming is a material SWAP, not a per-frame traverse + mutate.

export interface ObstacleMats { normal: THREE.MeshStandardMaterial; dim: THREE.MeshStandardMaterial }

function buildObstacleMaterial(opts: {
  fillColor: string; fillOpacity: number;
  fresColor: string; fresOpacity: number; fresIntensity: number;
  dim: number;
  // When an outline atlas is supplied it becomes the emissiveMap and the
  // emissive channel is repurposed for it (outlineColor × outlineEmission), so
  // only the face borders glow. Otherwise the emissive is a faint fill self-glow.
  outlineMap?: THREE.Texture | null;
  outlineColor?: string; outlineEmission?: number;
  // Wireframe style: drive the fragment ALPHA from the outline/border atlas so
  // only the edges are opaque (body see-through) — matches the game's
  // "Wireframe Obstacle" material (border texture only, no solid fill).
  wireframe?: boolean;
  // Synth VR walls: shade a right-hand instance as a left-hand one is shaded (see below).
  matchMirrored?: boolean;
}): THREE.MeshStandardMaterial {
  const hasOutline = !!opts.outlineMap;
  const matchMirrored = opts.matchMirrored ? 1 : 0;
  const m = new THREE.MeshStandardMaterial({
    color: new THREE.Color(opts.fillColor),
    emissive: new THREE.Color(hasOutline ? (opts.outlineColor || '#ffffff') : opts.fillColor),
    emissiveIntensity: (hasOutline ? (opts.outlineEmission ?? 1.5) : 0.15) * opts.dim,
    transparent: true,
    opacity: opts.fillOpacity * opts.dim,
    metalness: 0.1,
    roughness: 0.8,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  if (opts.outlineMap) m.emissiveMap = opts.outlineMap;
  const fresCol = new THREE.Color(opts.fresColor);
  const fresOpacity = opts.fresOpacity * opts.dim;
  const fresIntensity = opts.fresIntensity;
  const wire = !!opts.wireframe;
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uFresCol = { value: fresCol };
    shader.uniforms.uFresOpacity = { value: fresOpacity };
    shader.uniforms.uFresIntensity = { value: fresIntensity };
    shader.uniforms.uMatchMirrored = { value: matchMirrored };
    // A left wall is an instance drawn with a negative X scale, which reverses its winding: the face the
    // camera sees counts as a back face and is shaded with its normal flipped, so left walls come out
    // darker and purple while right walls are lit head-on. The same material shades both the same way
    // by flipping the normal of the instances that are not mirrored. A mesh that is not an instance (the
    // classic wall a selection or a drag draws) has its winding corrected by three whatever its scale, so
    // it is never "mirrored" here: it is always flipped, left or right, to match the pools.
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying float vMirrored;\nvoid main() {')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        #ifdef USE_INSTANCING
          vMirrored = determinant(mat3(instanceMatrix)) < 0.0 ? 1.0 : 0.0;
        #else
          vMirrored = 0.0;
        #endif`);
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {',
        'uniform vec3 uFresCol;\nuniform float uFresOpacity;\nuniform float uFresIntensity;\nuniform float uMatchMirrored;\nvarying float vMirrored;\nvoid main() {')
      .replace('#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
        if (uMatchMirrored > 0.5 && vMirrored < 0.5) normal = -normal;`)
      .replace('#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        float obsFres = pow(1.0 - saturate(abs(dot(normalize(vViewPosition), normal))), 2.0);
        totalEmissiveRadiance += uFresCol * obsFres * uFresIntensity * uFresOpacity;
        diffuseColor.a = clamp(diffuseColor.a + obsFres * uFresOpacity, 0.0, 1.0);
        ${wire ? `
        // Wireframe: the border atlas (white edges on black) IS the alpha mask,
        // so the solid body drops out and only the glowing edges remain.
        #ifdef USE_EMISSIVEMAP
          float wireA = texture2D( emissiveMap, vEmissiveMapUv ).r;
          diffuseColor.a = clamp(wireA * 1.4, 0.0, 1.0);
        #endif` : ''}`);
  };
  m.customProgramCacheKey = () => (wire ? 'obstacle-wireframe' : 'obstacle-fill-fresnel');
  return m;
}

/** Build a standalone obstacle-STYLE material tinted by `color` (used by the
 *  decor-wall layer so decorative walls look exactly like real obstacles — same
 *  folded-in fresnel rim + transparency, just a per-group fill tint). When
 *  `selected` it glows brighter so the picked group stands out.
 *
 *  `outlineMap` is the game's per-shape outline atlas (white edges on black,
 *  e.g. Mat_Color_RegularObstacle_Outlines.png). Fed as the emissiveMap so ONLY
 *  the face borders emit — and because emissive = the group colour, the outline
 *  glows in the chosen colour (option A). The base shader's emissivemap_fragment
 *  is preserved by buildObstacleMaterial's onBeforeCompile, so the map samples
 *  before the fresnel rim is added on top. Caller owns disposal. */
export function createDesignMaterial(color: string, gp: {
  obstacleFillOpacity: number;
  obstacleFresnelColor: string; obstacleFresnelOpacity: number; obstacleFresnelIntensity: number;
  obstacleOutlineEmission?: number;
}, selected: boolean, outlineMap?: THREE.Texture | null, wireframe = false): THREE.MeshStandardMaterial {
  const emission = gp.obstacleOutlineEmission ?? 1.5;
  if (wireframe) {
    // Edges-only style (matches the game's "Wireframe Obstacle"): the border
    // atlas drives alpha so the body is see-through and only the edges glow.
    const wm = buildObstacleMaterial({
      fillColor: color, fillOpacity: 1,
      fresColor: gp.obstacleFresnelColor, fresOpacity: 0, fresIntensity: 0, dim: 1,
      outlineMap: outlineMap ?? null, outlineColor: color,
      outlineEmission: emission * 1.6 * (selected ? 1.5 : 1),
      wireframe: true,
    });
    // See-through: don't occlude walls behind (no solid body to write depth for).
    wm.depthWrite = false;
    // No outline atlas for this shape → fall back to mesh-edge wireframe so the
    // wall is still visible rather than fully transparent.
    if (!outlineMap) {
      wm.wireframe = true;
      wm.opacity = 1;
      wm.emissive = new THREE.Color(color);
      wm.emissiveIntensity = 1.2 * (selected ? 1.5 : 1);
    }
    return wm;
  }
  // Design is DECORATION — meant to be seen at all times, not a subtle gameplay
  // hazard. So it renders with a clearly-visible fill FLOOR (well above the
  // faint real-obstacle fill), whether or not it's selected — otherwise an
  // unselected wall art reads as "not there" until you click it. Selection then
  // adds an EXTRA highlight (brighter fill + outline glow) on top, rather than
  // being the difference between visible and invisible.
  const baseOpacity = Math.min(1, gp.obstacleFillOpacity + 0.3);
  // Build WITH the outline at construction (same robust path as real obstacles),
  // but the outline colour is the per-group `color` (panel-controlled) rather
  // than the global obstacle outline colour.
  const m = buildObstacleMaterial({
    fillColor: color, fillOpacity: baseOpacity,
    fresColor: gp.obstacleFresnelColor, fresOpacity: gp.obstacleFresnelOpacity,
    fresIntensity: gp.obstacleFresnelIntensity, dim: 1,
    outlineMap: outlineMap ?? null, outlineColor: color, outlineEmission: emission,
  });
  if (selected) {
    m.emissiveIntensity = (outlineMap ? emission : 0.7) * 1.5;
    m.opacity = Math.min(1, baseOpacity + 0.25);
  }
  // Design is built from MANY overlapping solid shapes (a composite piece), so it
  // must occlude itself correctly — write depth. buildObstacleMaterial defaults
  // to depthWrite:false (right for the game's faint see-through hazards, wrong
  // here: without it the overlapping decor walls "mount" on top of each other
  // instead of nearer ones hiding farther ones). The fill is opaque enough
  // (≥0.45) that writing depth reads as solid; the fresnel rim + outline still
  // glow on top. depthWrite is safe with transparent:true — the depth TEST then
  // resolves occlusion regardless of the instances' draw order.
  m.depthWrite = true;
  return m;
}

// Materials are cached PER SHAPE now (each shape has its own outline atlas fed
// as the emissiveMap), keyed by shape. When any global obstacle setting changes
// the whole cache is disposed + rebuilt.
interface ObstacleGP {
  obstacleFillColor: string; obstacleFillOpacity: number;
  obstacleFresnelColor: string; obstacleFresnelOpacity: number; obstacleFresnelIntensity: number;
  obstacleOutlineColor: string; obstacleOutlineEmission: number;
}
let _obsSettingsKey = '';
const _obsMatsCache = new Map<string, ObstacleMats>();

export function getObstacleMaterials(gp: ObstacleGP, shape: ObstacleShape, outlineMap: THREE.Texture | null, matchMirrored = false): ObstacleMats {
  const settingsKey = `${gp.obstacleFillColor}|${gp.obstacleFillOpacity}|${gp.obstacleFresnelColor}|${gp.obstacleFresnelOpacity}|${gp.obstacleFresnelIntensity}|${gp.obstacleOutlineColor}|${gp.obstacleOutlineEmission}`;
  if (settingsKey !== _obsSettingsKey) {
    for (const m of _obsMatsCache.values()) { m.normal.dispose(); m.dim.dispose(); }
    _obsMatsCache.clear();
    _obsSettingsKey = settingsKey;
  }
  const cacheKey = matchMirrored ? `${shape}:match` : shape;
  const cached = _obsMatsCache.get(cacheKey);
  if (cached) return cached;
  const base = {
    matchMirrored,
    fillColor: gp.obstacleFillColor, fillOpacity: gp.obstacleFillOpacity,
    fresColor: gp.obstacleFresnelColor, fresOpacity: gp.obstacleFresnelOpacity,
    fresIntensity: gp.obstacleFresnelIntensity,
    outlineMap, outlineColor: gp.obstacleOutlineColor, outlineEmission: gp.obstacleOutlineEmission,
  };
  const mats: ObstacleMats = {
    normal: buildObstacleMaterial({ ...base, dim: 1 }),
    dim: buildObstacleMaterial({ ...base, dim: 0.2 }),
  };
  _obsMatsCache.set(cacheKey, mats);
  return mats;
}

// ── Central obstacle ticker ──────────────────────────────────────────────────
// Every mounted ObstacleModel registers here; the windowed renderer calls
// tickObstacles() once per frame — replacing N useFrame callbacks each doing a
// full traverse + material mutation with one flat loop + material swaps.

interface ObstacleEntry {
  beats: number;
  group: THREE.Group;
  meshes: THREE.Mesh[];
  matsRef: { current: ObstacleMats | null };  // this obstacle's shape material pair
  appliedMats: ObstacleMats | null;           // last pair the ticker pushed onto the meshes
  pulseStart: number;
  prevOnGrid: boolean;
  prevRel?: number;   // last frame's (beats - rawBeat) — to catch the trigger crossing
  dimmed: boolean;
  halo: { current: THREE.Mesh | null };      // this wall's halo (SRVR-0314), set by ObstacleFBX
}

const _obstacleReg = new Set<ObstacleEntry>();

export function tickObstacles(): void {
  if (_obstacleReg.size === 0) return;
  const store = useEditorStore.getState();
  const rawBeat = chartBeat();   // offset-adjusted chart beat (NOT the raw time beat)
  const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const gridTol = onBeatTolerance(store.activeView, store.scrollStep, store.beatmap?.bpm);
  // Synth VR: only a wall exactly on the beat pops; one the playhead jumps over does not.
  const popCrossing = !isSynthVrView(store.activeView);
  const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
  let vis = 0;

  for (const e of _obstacleReg) {
    e.group.position.z = -e.beats * BEAT_SPACING;

    // Draw-distance (FOV) cull: hide obstacles outside the adaptive horizon —
    // ahead past the FOV, and more than `behind` beats back (tied to the FOV so a
    // tight FOV also drops passed walls). The window pre-mounts within a margin so
    // a wall never hitches as it crosses into view.
    const rel = e.beats - rawBeat;
    e.group.visible = rel <= PERF.obstacleViewBeats && rel >= -behind;
    if (!e.group.visible) continue;
    vis++;

    // Grid-alignment pop: only a scrubbing/editing aid (confirms an obstacle is
    // exactly on the beat while you drag the playhead by hand). During actual
    // playback it stays off — the obstacle just passes through the trigger line
    // like real gameplay, no pop. Trigger state (onGrid/crossed) is still tracked
    // every frame regardless of play state, so the detector is already correct
    // the instant the user pauses/scrubs (no stale prevRel from a frame skip).
    const onGrid = Math.abs(rel) < gridTol;
    const crossed = popCrossing && e.prevRel !== undefined && e.prevRel > 0 && rel <= 0;
    const trig = onGrid || crossed;
    if (trig && !e.prevOnGrid) e.pulseStart = nowMs;
    e.prevOnGrid = trig;
    e.prevRel = rel;
    let pulse = 1;
    if (!store.playback.playing && e.pulseStart) {
      const dt = (nowMs - e.pulseStart) / 1000;
      const PD = 0.18;
      if (dt < PD) pulse = 1 + 0.4 * Math.sin(Math.PI * (dt / PD));
      else e.pulseStart = 0;
    }
    e.group.scale.setScalar(pulse);
    if (e.halo.current) e.halo.current.visible = haloOnGridNow(store, e.beats, rawBeat);

    // Behind the trigger + off the snap → swap to this obstacle's dimmed
    // material. Also re-apply when the material pair itself changed (settings
    // edit rebuilt the per-shape materials) so the swap state stays correct.
    const pair = e.matsRef.current;
    const dim = e.beats < rawBeat && !onGrid;
    if (pair && (pair !== e.appliedMats || dim !== e.dimmed)) {
      e.dimmed = dim;
      e.appliedMats = pair;
      const mat = dim ? pair.dim : pair.normal;
      // Synth VR: a wall behind the playhead (the dimmed one) is drawn but not pickable.
      const pickable = !(dim && isSynthVrView(store.activeView));
      for (const mesh of e.meshes) {
        mesh.material = mat;
        mesh.raycast = pickable ? THREE.Mesh.prototype.raycast : NO_RAYCAST;
      }
    }
  }
  // Stats for the classic (selected/dragging) obstacles the ticker still owns;
  // the instanced pools add their far-larger counts on top (see below). `vis` is
  // exposed so the pool writer can include the classic visibles in obsVisible.
  _classicObsVisible = vis;
}

// Visible count from the last tickObstacles() pass (classic selected obstacles),
// read by the instanced-pool writer so the HUD's obsVisible covers both paths.
let _classicObsVisible = 0;

// ── Extract all BufferGeometry from an FBX group (cached once per fbx path) ──

function extractGeometries(fbx: THREE.Group): THREE.BufferGeometry[] {
  const geoms: THREE.BufferGeometry[] = [];
  fbx.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      geoms.push((child as THREE.Mesh).geometry);
    }
  });
  return geoms;
}

// ── Per-obstacle config keys that we support ─────────────────────────────────

export const CONFIGURABLE_KEYS = new Set<string>([
  'wall_left', 'wall_right', 'angle_left', 'angle_right', 'center', 'crouch',
]);

// ── Where a wall is drawn ────────────────────────────────────────────────────

/**
 * World X, and Y above `NOTE_Y_OFFSET`, of a wall: its file position in world units plus its type's
 * Pos X / Pos Y (the desktop prefab's offset by default) — see `wallGeometry`. Nothing replaces the
 * file position: every wall is a wall, and the format's centre (0.002, 0.0012) is a real position, not
 * noise. The renderer, the gizmo and marquee hit-testing all draw through this ([SRVR-0064].AC5).
 */
export function wallDrawXY(
  position: ArrayLike<number> | null | undefined,
  cfg: { defaultX: number; defaultY: number } | null | undefined,
): [number, number] {
  return wallXY(position, cfg?.defaultX ?? 0, cfg?.defaultY ?? 0);
}

// ── Single Obstacle FBX Renderer ─────────────────────────────────────────────

function ObstacleFBX({ wallType, meshesOut, matsRef, haloRef, selected }: { wallType: string; meshesOut: THREE.Mesh[]; matsRef: { current: ObstacleMats | null }; haloRef: { current: THREE.Mesh | null }; selected: boolean }) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const obstacleScale = gp.obstacleScale;
  const configs = gp.obstacleConfigs;

  // Per-obstacle config (shape, scale, position, rotation)
  const cfg = CONFIGURABLE_KEYS.has(wallType)
    ? configs[wallType as ObstacleConfigKey]
    : DEFAULT_OBSTACLE_CONFIGS[wallType as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS.center;

  // Resolve shape → FBX/texture paths
  const shapeDef = OBSTACLE_SHAPE_FBX[cfg.shape];
  const fbx = useFBX(shapeDef.fbx);
  // The shape's outline atlas → emissiveMap (so only the face borders glow).
  const outline = useTexture(shapeDef.texture);
  useMemo(() => { if (outline) { outline.colorSpace = THREE.SRGBColorSpace; outline.needsUpdate = true; } }, [outline]);

  // Extract geometries once from the loaded FBX (stable reference)
  const geometries = useMemo(() => extractGeometries(fbx), [fbx]);

  // Per-shape materials (normal + dimmed), rebuilt when a global obstacle
  // setting changes. Exposed to the parent's ticker entry via matsRef. The list names the settings
  // the materials read rather than `gp` itself, so a change to anything else in `gp` rebuilds
  // nothing; the rule cannot see that, and reports at the array, which is the line below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // A selected or dragged wall is drawn here rather than by the pools, so it needs the same
  // mirrored-shading match the pools use in Synth VR, or a left wall changes colour when picked.
  const matchMirrored = isVr() && SIDED_SHAPES.has(cfg.shape);
  const mats = useMemo(() => getObstacleMaterials(gp, cfg.shape, outline, matchMirrored), [
    gp.obstacleFillColor, gp.obstacleFillOpacity,
    gp.obstacleFresnelColor, gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity,
    gp.obstacleOutlineColor, gp.obstacleOutlineEmission, cfg.shape, outline, matchMirrored,
  ]);
  matsRef.current = mats;

  // Halo (SRVR-0314): the same ring the pools draw, inside the same scale/rotation group, so it sits
  // on this wall exactly. The ticker shows it while the wall is on the grid.
  const halo = useMemo(() => {
    const merged = getObstacleMergedGeometry(cfg.shape, fbx);
    return merged ? makeHaloMesh(wallHaloGeometry(merged), false, haloColor(gp.obstacleFillColor), 'wall') : null;
  }, [cfg.shape, fbx, gp.obstacleFillColor]);
  useEffect(() => {
    haloRef.current = halo;
    return () => { haloRef.current = null; if (halo) (halo.material as THREE.Material).dispose(); };
  }, [halo, haloRef]);

  // Mirror left variants (wall_left, angle_left have negative scaleX historically)
  const isLeft = wallType === 'wall_left' || wallType === 'angle_left';
  const baseX = isLeft ? -obstacleScale : obstacleScale;
  const sx = baseX * (cfg.scaleX ?? 1);
  const sy = obstacleScale * (cfg.scaleY ?? 1);
  const sz = obstacleScale * (cfg.scaleZ ?? 1);

  // The white rim a selected wall wears, inside the same scale/rotation group so it sits on the wall.
  const rimScale = (Math.abs(sx) + Math.abs(sy)) / 2;
  const rim = useMemo(() => {
    const merged = selected ? getObstacleMergedGeometry(cfg.shape, fbx) : null;
    return merged ? wallRimGeometry(merged, rimScale) : null;
  }, [selected, cfg.shape, fbx, rimScale]);

  const rotZ = (cfg.rotation * Math.PI) / 180; // degrees → radians

  // Collect mesh refs for the ticker's material swap.
  const collect = (mesh: THREE.Mesh | null) => {
    if (mesh && !meshesOut.includes(mesh)) meshesOut.push(mesh);
  };

  return (
    <group scale={[sx, sy, sz]} rotation={[0, 0, rotZ]}>
      {geometries.map((geom, i) => (
        <mesh key={i} ref={collect} geometry={geom} material={mats.normal} />
      ))}
      {halo && <primitive object={halo} />}
      {rim && <mesh geometry={rim} material={WALL_RIM_MATERIAL} raycast={() => null} renderOrder={10} />}
    </group>
  );
}

// ── Fallback Line Marker (for unknown wall types) ────────────────────────────

function FallbackSlideMarker({ color }: { color: string }) {
  const { color: rgb, alpha } = parseWallColor(color);

  return (
    <>
      <mesh position={[0, -0.5, 0]}>
        <boxGeometry args={[TUNNEL_WIDTH, 0.04, 0.04]} />
        <meshStandardMaterial
          color={rgb}
          emissive={rgb}
          emissiveIntensity={0.4}
          transparent
          opacity={alpha * 0.6}
        />
      </mesh>
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[TUNNEL_WIDTH, 0.04, 0.04]} />
        <meshStandardMaterial
          color={rgb}
          emissive={rgb}
          emissiveIntensity={0.4}
          transparent
          opacity={alpha * 0.6}
        />
      </mesh>
    </>
  );
}

// ── Instanced obstacle pools ─────────────────────────────────────────────────
// Every NON-selected wall obstacle renders through per-shape InstancedMesh pools
// (a normal + a dimmed pool per shape) instead of an individual group-of-meshes
// each. Result: a constant handful of draw calls for the whole dense section
// (≤ 5 shapes × 2 pools) rather than N × (sub-mesh count). Mirrors the note
// InstancedNotePools: ONE writer composes every visible instance's matrix each
// frame, classifies normal vs dim by beat, compacts into pool slots, and sets
// mesh.count. Selected / dragging obstacles are EXCLUDED (WindowedSlides renders
// those as the classic ObstacleModel with its highlight + gizmo) — always few.
//
// The pools live inside <BeatScrollGroup> (like the notes), so each instance
// sits at a static z = -beat·BEAT_SPACING and the parent group's per-frame
// translation scrolls them — no per-frame z bookkeeping here.

const DEG2RAD = Math.PI / 180;
const OBS_POOL_LIMIT = 320;            // ≥ PERF.maxObstacles ceiling (300)
// Every shape a wall_type can resolve to (the 6 configurable types map onto
// regular / diagonal / center; square / triangle are here in case a wall is
// re-shaped in settings). Fixed length → stable hook order.
const WALL_SHAPES: ObstacleShape[] = ['regular', 'diagonal', 'center', 'square', 'triangle'];
type ObsPool = 'normal' | 'dim';
const OBS_POOLS: ObsPool[] = ['normal', 'dim'];

// Merged geometry per shape — the whole shape as a single buffer so it's one
// instanced draw. CRITICAL: we merge EXACTLY the geometries the classic
// ObstacleFBX renders (extractGeometries → raw child geometry, in local space).
// We must NOT bake the FBX node/root world matrix (as mergeFbxGeometry does):
// obstacleScale + the per-type scaleX/Y/Z are calibrated against this raw
// geometry, so baking the root transform and THEN applying that scale would
// double-transform the wall — it renders stretched / mis-rotated. Merging the
// raw geometries makes the instanced wall byte-identical to the classic one.
// `rotX` bakes a base X-rotation into the merged buffer. Every obstacle FBX in
// use is authored upright — raw local geometry thinnest along Z, face in the XY
// plane facing the player — so all callers pass 0. Cached per (shape, rotX).
const _obsMergedGeom = new Map<string, THREE.BufferGeometry>();
export function getObstacleMergedGeometry(shape: ObstacleShape, fbx: THREE.Group, rotX = 0): THREE.BufferGeometry | null {
  const key = `${shape}:${rotX}`;
  const cached = _obsMergedGeom.get(key);
  if (cached) return cached;
  const raw = extractGeometries(fbx);
  if (!raw.length) return null;
  // Normalise each sub-geometry to a consistent {position, normal, uv} non-indexed
  // buffer so mergeGeometries can concatenate them (raw local space — no matrix).
  const parts = raw.map((g) => {
    const src = g.index ? g.toNonIndexed() : g.clone();
    const pos = src.getAttribute('position') as THREE.BufferAttribute;
    const out = new THREE.BufferGeometry();
    out.setAttribute('position', pos.clone());
    let nrm = src.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!nrm) { src.computeVertexNormals(); nrm = src.getAttribute('normal') as THREE.BufferAttribute; }
    out.setAttribute('normal', nrm.clone());
    const uv = src.getAttribute('uv') as THREE.BufferAttribute | undefined;
    out.setAttribute('uv', uv ? uv.clone() : new THREE.BufferAttribute(new Float32Array(pos.count * 2), 2));
    return out;
  });
  const merged = parts.length === 1 ? parts[0] : (mergeGeometries(parts, false) ?? parts[0]);
  if (rotX) merged.rotateX(rotX);
  merged.computeBoundingSphere();
  _obsMergedGeom.set(key, merged);
  return merged;
}

// Reused scratch (no per-instance allocation in the writer).
const _oPos = new THREE.Vector3();
const _oQuat = new THREE.Quaternion();
const _oEuler = new THREE.Euler();
const _oScale = new THREE.Vector3();
const _oMatrix = new THREE.Matrix4();

/** The new selection behaviours (the rim, the right-button actions, no click-select) belong to the Synth VR
 *  view; Console shares these walls and keeps the old ones. */
const isVr = () => isSynthVrView(useEditorStore.getState().activeView);

/** The selection rim of the walls drawn from instanced pools (squares, triangles, crouches): one
 *  instanced mesh per pool, each rim written with the wall's own matrix. It never takes a pick. */
class RimPool {
  readonly mesh: THREE.InstancedMesh;
  private n = 0;
  constructor(wallGeo: THREE.BufferGeometry, worldScale: number, name: string) {
    this.mesh = new THREE.InstancedMesh(wallRimGeometry(wallGeo, worldScale), WALL_RIM_MATERIAL, OBS_POOL_LIMIT);
    this.mesh.name = name;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
    this.mesh.raycast = () => {};
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
  }
  begin() { this.n = 0; }
  add(wall: THREE.Matrix4) { if (this.n < OBS_POOL_LIMIT) this.mesh.setMatrixAt(this.n++, wall); }
  end() { this.mesh.count = this.n; this.mesh.instanceMatrix.needsUpdate = true; }
  dispose() { this.mesh.dispose(); }   // the geometry and material are shared
}

interface ObsPopState { pulseStart: number; prevOnGrid: boolean; prevRel: number }

/**
 * The grid-alignment pop of one wall: a brief enlargement as it reaches the playhead beat, by landing on it
 * or, when `crossing` is on, by being crossed by the playhead (Synth VR turns that off: only a wall exactly
 * on the beat pops). Returns the scale factor (1 when idle). Off during playback — a wall
 * just passes through the trigger as in the game — but the crossing is tracked every frame, so the
 * detector is right the moment the track is paused. `pop` holds the state per wall, keyed by `key`.
 */
function obstaclePop<K>(
  pop: Map<K, ObsPopState>, key: K, rel: number, gridTol: number, nowMs: number, playing: boolean, crossing = true,
): number {
  const onGrid = Math.abs(rel) < gridTol;
  let ps = pop.get(key);
  if (!ps) { ps = { pulseStart: 0, prevOnGrid: false, prevRel: rel }; pop.set(key, ps); }
  const crossed = crossing && ps.prevRel > 0 && rel <= 0;
  const trig = onGrid || crossed;
  if (trig && !ps.prevOnGrid) ps.pulseStart = nowMs;
  ps.prevOnGrid = trig;
  ps.prevRel = rel;
  if (playing || !ps.pulseStart) return 1;
  const dt = (nowMs - ps.pulseStart) / 1000;
  const PD = 0.18;
  if (dt < PD) return 1 + 0.4 * Math.sin(Math.PI * (dt / PD));
  ps.pulseStart = 0;
  return 1;
}

// The slide-wall shapes Synth VR shades alike: Left, Right, diagonal and the centre wall. The squares,
// triangles and crouches are matched where they are drawn.
export const SIDED_SHAPES = new Set<ObstacleShape>(['regular', 'diagonal', 'center']);

export function InstancedObstaclePools({ entries }: { entries: Array<{ slide: Slide; index: number }> }) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const vr = useEditorStore(s => isSynthVrView(s.activeView));

  // Load every wall shape (FBX + outline atlas). Preloaded at module scope, so
  // this is cheap and unconditional (stable hook order — never in a loop).
  const fbxRegular  = useFBX(OBSTACLE_SHAPE_FBX.regular.fbx);
  const fbxDiagonal = useFBX(OBSTACLE_SHAPE_FBX.diagonal.fbx);
  const fbxCenter   = useFBX(OBSTACLE_SHAPE_FBX.center.fbx);
  const fbxSquare   = useFBX(OBSTACLE_SHAPE_FBX.square.fbx);
  const fbxTriangle = useFBX(OBSTACLE_SHAPE_FBX.triangle.fbx);
  const texRegular  = useTexture(OBSTACLE_SHAPE_FBX.regular.texture);
  const texDiagonal = useTexture(OBSTACLE_SHAPE_FBX.diagonal.texture);
  const texCenter   = useTexture(OBSTACLE_SHAPE_FBX.center.texture);
  const texSquare   = useTexture(OBSTACLE_SHAPE_FBX.square.texture);
  const texTriangle = useTexture(OBSTACLE_SHAPE_FBX.triangle.texture);

  const fbxByShape = useMemo<Record<ObstacleShape, THREE.Group>>(() => ({
    regular: fbxRegular, diagonal: fbxDiagonal, center: fbxCenter, square: fbxSquare, triangle: fbxTriangle,
  }), [fbxRegular, fbxDiagonal, fbxCenter, fbxSquare, fbxTriangle]);
  const texByShape = useMemo<Record<ObstacleShape, THREE.Texture>>(() => {
    const rec = { regular: texRegular, diagonal: texDiagonal, center: texCenter, square: texSquare, triangle: texTriangle };
    for (const t of Object.values(rec)) { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true; } }
    return rec;
  }, [texRegular, texDiagonal, texCenter, texSquare, texTriangle]);

  const geoByShape = useMemo(() => {
    const out: Partial<Record<ObstacleShape, THREE.BufferGeometry | null>> = {};
    for (const shape of WALL_SHAPES) out[shape] = getObstacleMergedGeometry(shape, fbxByShape[shape]);
    return out;
  }, [fbxByShape]);

  // Shared obstacle materials (normal + dim) per shape — cached module-side and
  // shared with any classic selected obstacle, so a settings edit rebuilds once.
  const matsByShape = useMemo(() => {
    const out: Partial<Record<ObstacleShape, ObstacleMats>> = {};
    // Synth VR: the Left and Right variants are shaded alike. The shapes the sided walls use only.
    for (const shape of WALL_SHAPES) out[shape] = getObstacleMaterials(gp, shape, texByShape[shape], vr && SIDED_SHAPES.has(shape));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gp.obstacleFillColor, gp.obstacleFillOpacity, gp.obstacleFresnelColor,
      gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity, gp.obstacleOutlineColor,
      gp.obstacleOutlineEmission, texByShape, vr]);

  // Halos for the pooled walls on the grid (SRVR-0314): one pool per shape, every wall the fill colour.
  const haloMat = useMemo(() => makeHaloMaterial({ billboard: false, color: haloColor(gp.obstacleFillColor) }), [gp.obstacleFillColor]);
  useEffect(() => () => haloMat.dispose(), [haloMat]);
  const halos = useMemo(() => {
    const out: Partial<Record<ObstacleShape, HaloPool>> = {};
    for (const shape of WALL_SHAPES) {
      const geo = geoByShape[shape];
      if (geo) out[shape] = new HaloPool(wallHaloGeometry(geo), haloMat, `halo-walls-${shape}`, false, 'wall');
    }
    return out;
  }, [geoByShape, haloMat]);
  useEffect(() => () => { for (const h of Object.values(halos)) h?.dispose(); }, [halos]);

  const meshRefs = useRef<Record<string, THREE.InstancedMesh | null>>({});
  const slotMaps = useRef<Record<string, Int32Array>>(null!);
  if (!slotMaps.current) {
    const m: Record<string, Int32Array> = {};
    for (const shape of WALL_SHAPES) for (const p of OBS_POOLS) m[`${shape}-${p}`] = new Int32Array(OBS_POOL_LIMIT);
    slotMaps.current = m;
  }
  // Per-obstacle grid-pop state, keyed by absolute slide index (edit/scrub only).
  const popState = useRef(new Map<number, ObsPopState>());
  const counts = useRef<Record<string, number>>({});

  useEffect(() => {
    for (const k of Object.keys(meshRefs.current)) {
      const m = meshRefs.current[k];
      if (m) m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoByShape, matsByShape]);

  // ── The writer: place + classify every visible obstacle each frame ──
  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const gridTol = onBeatTolerance(store.activeView, store.scrollStep, store.beatmap?.bpm);
    // Synth VR: only a wall exactly on the beat pops; one the playhead jumps over does not.
    const popCrossing = !isSynthVrView(store.activeView);
    const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
    const playing = store.playback.playing;
    const obstacleScale = gp.obstacleScale;
    const configs = gp.obstacleConfigs;
    const pop = popState.current;
    const c = counts.current;
    for (const shape of WALL_SHAPES) for (const p of OBS_POOLS) c[`${shape}-${p}`] = 0;
    for (const shape of WALL_SHAPES) halos[shape]?.begin();

    let vis = 0;
    // Iterate FAR → NEAR (entries are sorted near→far by beat, so walk it in
    // reverse). A single InstancedMesh draws its instances in SLOT order, so we
    // have to bake the back-to-front transparent order into the slots ourselves —
    // otherwise overlapping see-through walls blend in the wrong order and their
    // outlines pile up / show through each other. (The classic per-mesh path got
    // this ordering for free from three's transparent sort.)
    for (let _ei = entries.length - 1; _ei >= 0; _ei--) {
      const { slide, index } = entries[_ei];
      const wt = slide.wall_type;
      if (!CONFIGURABLE_KEYS.has(wt)) continue;
      const cfg = configs[wt as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS[wt as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS.center;
      const shape = cfg.shape;
      const geo = geoByShape[shape];
      if (!geo) continue;

      const beat = slide.time_beats;
      const rel = beat - rawBeat;
      // Draw-distance (FOV) cull: only instances within the horizon are written.
      if (rel > PERF.obstacleViewBeats || rel < -behind) continue;

      // Grid-alignment pop (edit/scrub aid; off during playback). Same crossing +
      // onGrid trigger as the classic ticker, tracked per absolute index.
      const onGrid = Math.abs(rel) < gridTol;
      const pulse = obstaclePop(pop, index, rel, gridTol, nowMs, playing, popCrossing);

      const dim = beat < rawBeat && !onGrid;
      const poolKey = `${shape}-${dim ? 'dim' : 'normal'}`;
      const mesh = meshRefs.current[poolKey];
      if (!mesh) continue;
      const slot = c[poolKey];
      if (slot >= OBS_POOL_LIMIT) continue;

      const [wx, wy] = wallDrawXY(slide.position, cfg);
      const isLeft = wt === 'wall_left' || wt === 'angle_left';
      const bs = obstacleScale * pulse;
      _oPos.set(wx, NOTE_Y_OFFSET + wy, -beat * BEAT_SPACING);
      // Per-obstacle roll from the .synth (zRotation, e.g. 90°/270° — most VR
      // walls carry one) ON TOP of the wall-type's default rotation. Without it,
      // walls the game shows "lying down" render upright.
      _oEuler.set(0, 0, (cfg.rotation + (slide.z_rotation ?? 0)) * DEG2RAD);
      _oQuat.setFromEuler(_oEuler);
      _oScale.set(
        (isLeft ? -bs : bs) * (cfg.scaleX ?? 1),
        bs * (cfg.scaleY ?? 1),
        bs * (cfg.scaleZ ?? 1),
      );
      _oMatrix.compose(_oPos, _oQuat, _oScale);
      mesh.setMatrixAt(slot, _oMatrix);
      slotMaps.current[poolKey][slot] = index;
      c[poolKey] = slot + 1;
      vis++;
      if (haloOnGridNow(store, beat, rawBeat)) halos[shape]?.add(_oMatrix);
    }
    for (const shape of WALL_SHAPES) halos[shape]?.end();

    for (const shape of WALL_SHAPES) {
      for (const p of OBS_POOLS) {
        const m = meshRefs.current[`${shape}-${p}`];
        if (!m) continue;
        m.count = c[`${shape}-${p}`];
        m.instanceMatrix.needsUpdate = true;
        dropPickBounds(m);
      }
    }
    // Prune stale pop state so the map can't grow without bound over a session.
    if (pop.size > 1024) pop.clear();

    RENDER_STATS.obsVisible = vis + _classicObsVisible;
    RENDER_STATS.obsMounted = entries.length + _obstacleReg.size;
  });

  // ── Events (instanceId → absolute slide index), mirroring ObstacleModel ──
  const makeHandlers = (key: string) => ({
    onClick: (e: ThreeEvent<MouseEvent>) => {
      const id = (e as { instanceId?: number }).instanceId;
      if (id == null) return;
      const index = slotMaps.current[key]?.[id];
      if (index == null) return;
      const s = useEditorStore.getState();
      if (!selectToolAvailable(s.activeView) || s.toolMode !== 'select') return;
      e.stopPropagation();
      const cur = s.selectedSlideIds;
      if (e.nativeEvent.shiftKey) {
        s.selectSlides(cur.includes(index) ? cur.filter(i => i !== index) : [...cur, index]);
      } else {
        s.selectNotes([]);
        s.selectSlides([index]);
      }
    },
    // Right-button actions run on the press, never on the context menu event that follows it: that
    // second event would act on whatever the first one uncovered behind the object (it deleted two).
    onContextMenu: (e: ThreeEvent<MouseEvent>) => {
      if (isVr()) { e.nativeEvent.preventDefault(); return; }
      // Other views: the context menu deletes the wall, as it always did there.
      const id = (e as { instanceId?: number }).instanceId;
      if (id == null) return;
      const index = slotMaps.current[key]?.[id];
      if (index == null) return;
      e.stopPropagation();
      e.nativeEvent.preventDefault();
      useEditorStore.getState().removeSlide(index);
    },
    // Alt+press: drag the wall anywhere (objectDrag.ts), whatever the tool. A right press is Alt =
    // go to the wall's beat, Ctrl = Remote Delete, and always stops here, on the first object hit.
    onPointerDown: (e: ThreeEvent<PointerEvent>) => {
      const id = (e as { instanceId?: number }).instanceId;
      if (id == null) return;
      const index = slotMaps.current[key]?.[id];
      if (index == null) return;
      if (e.button === 2 && isVr()) {
        e.stopPropagation();
        if (isAltRightPress(e)) {
          const st = useEditorStore.getState();
          const s = st.beatmap?.difficulties[st.activeDifficulty]?.slides[index];
          if (s) jumpPlayheadToObject(s.time_beats);
        } else if (isCtrlRightPress(e)) {
          useEditorStore.getState().removeSlide(index);
        }
        return;
      }
      startObjectDrag(e, { kind: 'slide', index });
    },
  });

  return (
    <>
      {WALL_SHAPES.map((shape) => {
        const geo = geoByShape[shape];
        const mat = matsByShape[shape];
        if (!geo || !mat) return null;
        return (
          <React.Fragment key={shape}>
            <instancedMesh
              ref={(m) => { meshRefs.current[`${shape}-normal`] = m; }}
              args={[geo, mat.normal, OBS_POOL_LIMIT]}
              frustumCulled={false}
              {...makeHandlers(`${shape}-normal`)}
            />
            <instancedMesh
              ref={(m) => { meshRefs.current[`${shape}-dim`] = m; }}
              args={[geo, mat.dim, OBS_POOL_LIMIT]}
              frustumCulled={false}
              raycast={dimPoolRaycast}   // the dim pool is the walls behind the playhead: drawn, not pickable
              {...makeHandlers(`${shape}-dim`)}
            />
            {halos[shape] && <primitive object={halos[shape]!.mesh} />}
          </React.Fragment>
        );
      })}
    </>
  );
}

// ── Instanced Square / Triangle walls — VR ───────────────────────────────────
// Square and Triangle are walls like the others; the .synth format just stores
// them in their own `Squares` / `Triangles` lists rather than in `Slides`, so
// they are drawn from those lists here, with the real VR shapes
// (SquareObstacle2 / TriangleObstacle2), upright as authored and rolled by each
// wall's own zRotation, at its file position. One instanced draw per shape.

const SQUARE_TRIANGLE_SHAPES: ObstacleShape[] = ['square', 'triangle'];

// Base X-rotation baked into the Square/Triangle merged geometry. Zero: the raw
// SquareObstacle2 / TriangleObstacle2 geometry is already upright (89×89×8.7 and
// 109×95×9.0, thinnest along Z, like the wall FBX). A Math.PI/2 here once tipped
// them onto their backs — see CAND-AA.
export const SQUARE_TRIANGLE_BASE_ROT_X = 0;

export function InstancedSquareTrianglePools({ entries }: {
  entries: Array<{ ob: Obstacle; shape: ObstacleShape; index: number }>;
}) {
  const gp = useEditorStore(s => s.gameplaySettings);

  const fbxSquare   = useFBX(OBSTACLE_SHAPE_FBX.square.fbx);
  const fbxTriangle = useFBX(OBSTACLE_SHAPE_FBX.triangle.fbx);
  const texSquare   = useTexture(OBSTACLE_SHAPE_FBX.square.texture);
  const texTriangle = useTexture(OBSTACLE_SHAPE_FBX.triangle.texture);

  const texByShape = useMemo<Record<string, THREE.Texture>>(() => {
    const rec = { square: texSquare, triangle: texTriangle };
    for (const t of Object.values(rec)) { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true; } }
    return rec;
  }, [texSquare, texTriangle]);

  const geoByShape = useMemo(() => ({
    square: getObstacleMergedGeometry('square', fbxSquare, SQUARE_TRIANGLE_BASE_ROT_X),
    triangle: getObstacleMergedGeometry('triangle', fbxTriangle, SQUARE_TRIANGLE_BASE_ROT_X),
  } as Partial<Record<ObstacleShape, THREE.BufferGeometry | null>>), [fbxSquare, fbxTriangle]);

  // Synth VR: shaded as the Left walls are, so every wall reads the same purple.
  const vr = useEditorStore(s => isSynthVrView(s.activeView));
  const matsByShape = useMemo(() => ({
    square: getObstacleMaterials(gp, 'square', texByShape.square, vr),
    triangle: getObstacleMaterials(gp, 'triangle', texByShape.triangle, vr),
  } as Partial<Record<ObstacleShape, ObstacleMats>>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gp.obstacleFillColor, gp.obstacleFillOpacity, gp.obstacleFresnelColor,
     gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity, gp.obstacleOutlineColor,
     gp.obstacleOutlineEmission, texByShape, vr]);

  // Halos for the squares and triangles on the grid (SRVR-0314), in the wall fill colour.
  const haloMat = useMemo(() => makeHaloMaterial({ billboard: false, color: haloColor(gp.obstacleFillColor) }), [gp.obstacleFillColor]);
  useEffect(() => () => haloMat.dispose(), [haloMat]);
  const halos = useMemo(() => {
    const out: Partial<Record<ObstacleShape, HaloPool>> = {};
    for (const shape of SQUARE_TRIANGLE_SHAPES) {
      const geo = geoByShape[shape];
      if (geo) out[shape] = new HaloPool(wallHaloGeometry(geo), haloMat, `halo-walls-${shape}-list`, false, 'wall');
    }
    return out;
  }, [geoByShape, haloMat]);
  useEffect(() => () => { for (const h of Object.values(halos)) h?.dispose(); }, [halos]);
  const rims = useMemo(() => {
    const out: Partial<Record<ObstacleShape, RimPool>> = {};
    for (const shape of SQUARE_TRIANGLE_SHAPES) {
      const geo = geoByShape[shape];
      if (!geo) continue;
      const d = desktopWallDefaults(shape as 'square' | 'triangle');
      out[shape] = new RimPool(geo, gp.obstacleScale * (d.scaleX + d.scaleY) / 2, `selected-rim-${shape}`);
    }
    return out;
  }, [geoByShape, gp.obstacleScale]);
  useEffect(() => () => { for (const r of Object.values(rims)) r?.dispose(); }, [rims]);

  const meshRefs = useRef<Record<string, THREE.InstancedMesh | null>>({});
  const counts = useRef<Record<string, number>>({});
  // Per-wall grid-pop state, keyed `${shape}:${index}`.
  const pop = useRef(new Map<string, ObsPopState>());
  // Per shape, a normal pool and a dim pool (the walls behind the playhead, drawn faded as every other
  // wall is), keyed `${shape}-${pool}`.
  // instanceId -> index in that shape's own list (`squares` / `triangles`), for picking.
  const slotMaps = useRef<Record<string, Int32Array>>(null!);
  if (!slotMaps.current) {
    slotMaps.current = Object.fromEntries(SQUARE_TRIANGLE_SHAPES.flatMap(sh =>
      OBS_POOLS.map(p => [`${sh}-${p}`, new Int32Array(OBS_POOL_LIMIT)])));
  }

  useEffect(() => {
    for (const k of Object.keys(meshRefs.current)) {
      const m = meshRefs.current[k];
      if (m) m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoByShape, matsByShape]);

  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
    const obstacleScale = gp.obstacleScale;
    const c = counts.current;
    for (const shape of SQUARE_TRIANGLE_SHAPES) {
      for (const p of OBS_POOLS) c[`${shape}-${p}`] = 0;
      halos[shape]?.begin(); rims[shape]?.begin();
    }
    const selectedBy = { square: new Set(store.selectedSquares), triangle: new Set(store.selectedTriangles) };
    const gridTol = onBeatTolerance(store.activeView, store.scrollStep, store.beatmap?.bpm);
    // Synth VR: only a wall exactly on the beat pops; one the playhead jumps over does not.
    const popCrossing = !isSynthVrView(store.activeView);
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const playing = store.playback.playing;
    if (pop.current.size > 1024) pop.current.clear();   // keep the map from growing over a session

    // Far → near, the transparent blend order.
    for (let _ei = entries.length - 1; _ei >= 0; _ei--) {
      const { ob, shape, index } = entries[_ei];
      const geo = geoByShape[shape];
      if (!geo) continue;
      const beat = ob.time_beats ?? 0;
      const rel = beat - rawBeat;
      if (rel > PERF.obstacleViewBeats || rel < -behind) continue;
      // Behind the playhead and off the grid: the dim pool, as for every other wall.
      const poolKey = `${shape}-${beat < rawBeat && Math.abs(rel) >= gridTol ? 'dim' : 'normal'}`;
      const mesh = meshRefs.current[poolKey];
      if (!mesh) continue;
      const slot = c[poolKey];
      if (slot >= OBS_POOL_LIMIT) continue;
      const pulse = obstaclePop(pop.current, `${shape}:${index}`, rel, gridTol, nowMs, playing, popCrossing);

      // Squares and triangles have no Per-Obstacle settings, so their desktop offset and size are
      // applied directly: file position + offset, rotated about that point ([SRVR-0064]).
      const d = desktopWallDefaults(shape as 'square' | 'triangle');
      const [ax, ay] = wallXY(ob.position, d.defaultX, d.defaultY);
      _oPos.set(ax, ay + NOTE_Y_OFFSET, -beat * BEAT_SPACING);
      _oEuler.set(0, 0, (ob.z_rotation ?? 0) * DEG2RAD);
      _oQuat.setFromEuler(_oEuler);
      _oScale.set(obstacleScale * pulse * d.scaleX, obstacleScale * pulse * d.scaleY, obstacleScale * pulse);
      _oMatrix.compose(_oPos, _oQuat, _oScale);
      mesh.setMatrixAt(slot, _oMatrix);
      slotMaps.current[poolKey][slot] = index;
      c[poolKey] = slot + 1;
      if (haloOnGridNow(store, beat, rawBeat)) halos[shape]?.add(_oMatrix);
      if (selectedBy[shape as 'square' | 'triangle'].has(index)) rims[shape]?.add(_oMatrix);
    }
    for (const shape of SQUARE_TRIANGLE_SHAPES) { halos[shape]?.end(); rims[shape]?.end(); }

    for (const shape of SQUARE_TRIANGLE_SHAPES) {
      for (const p of OBS_POOLS) {
        const m = meshRefs.current[`${shape}-${p}`];
        if (!m) continue;
        m.count = c[`${shape}-${p}`];
        m.instanceMatrix.needsUpdate = true;
        dropPickBounds(m);
      }
    }
  });

  // Pointer handling, per pool: instanceId -> index in the shape's own list.
  const pointerDown = (shape: ObstacleShape, poolKey: string) => (e: ThreeEvent<PointerEvent>) => {
    const id = (e as { instanceId?: number }).instanceId;
    if (id == null || id >= (counts.current[poolKey] ?? 0)) return;
    const index = slotMaps.current[poolKey][id];
    if (e.button === 2 && isVr()) {   // a right press stops on the first object hit
      e.stopPropagation();
      if (isCtrlRightPress(e)) {   // Ctrl + right-click: Remote Delete
        useEditorStore.getState().removeSquareTriangle(shape as 'square' | 'triangle', index);
        return;
      }
      if (!isAltRightPress(e)) return;
      // Alt + right-click: go to this wall's beat
      const st = useEditorStore.getState();
      const list = shape === 'square' ? st.beatmap?.difficulties[st.activeDifficulty]?.squares : st.beatmap?.difficulties[st.activeDifficulty]?.triangles;
      const o = list?.[index];
      if (o) jumpPlayheadToObject(o.time_beats ?? 0);
      return;
    }
    startObjectDrag(e, { kind: shape as 'square' | 'triangle', index });
  };

  return (
    <>
      {SQUARE_TRIANGLE_SHAPES.map((shape) => {
        const geo = geoByShape[shape];
        const mat = matsByShape[shape];
        if (!geo || !mat) return null;
        return (
          <React.Fragment key={shape}>
          {halos[shape] && <primitive object={halos[shape]!.mesh} />}
          {rims[shape] && <primitive object={rims[shape]!.mesh} />}
          <instancedMesh
            ref={(m) => { meshRefs.current[`${shape}-normal`] = m; }}
            args={[geo, mat.normal, OBS_POOL_LIMIT]}
            frustumCulled={false}
            onPointerDown={pointerDown(shape, `${shape}-normal`)}
          />
          <instancedMesh
            ref={(m) => { meshRefs.current[`${shape}-dim`] = m; }}
            args={[geo, mat.dim, OBS_POOL_LIMIT]}
            frustumCulled={false}
            raycast={dimPoolRaycast}   // the walls behind the playhead: drawn faded, not pickable
            onPointerDown={pointerDown(shape, `${shape}-dim`)}
          />
          </React.Fragment>
        );
      })}
    </>
  );
}

// ── Instanced CROUCH walls — VR ──────────────────────────────────────────────
// A crouch is a wall like any other: the desktop CrouchWrap prefab draws the Left/Right wall mesh,
// rotated 90° and raised over the player, at the crouch's file position. The difficulty keeps crouches
// as a time list with their positions alongside in `crouch_obstacles` (crouchWallEntries pairs them).
// Drawn with the crouch Per-Obstacle settings exactly as the wall pool draws a `wall_type: 'crouch'`
// slide, so the two can never disagree. Builds before this drew every crouch as a flat bar on the
// floor of the track (CAND-AH).
//
// Windowed: the time list is sorted, so the visible range is found by binary search and only those
// crouches are written — a dense map's hundreds of crouches cost nothing off screen.

export function InstancedCrouchWalls({ crouches, crouchObstacles, bpm }: {
  crouches: number[];
  crouchObstacles: Obstacle[] | undefined;
  bpm: number;
}) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const cfg = gp.obstacleConfigs.crouch ?? DEFAULT_OBSTACLE_CONFIGS.crouch;
  const shape = cfg.shape;

  const fbx = useFBX(OBSTACLE_SHAPE_FBX[shape].fbx);
  const tex = useTexture(OBSTACLE_SHAPE_FBX[shape].texture);
  const geo = useMemo(() => getObstacleMergedGeometry(shape, fbx), [shape, fbx]);
  const vr = useEditorStore(s => isSynthVrView(s.activeView));   // Synth VR: shaded as the Left walls are
  const mats = useMemo(() => {
    if (tex) { tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true; }
    return getObstacleMaterials(gp, shape, tex, vr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, tex, vr, gp.obstacleFillColor, gp.obstacleFillOpacity, gp.obstacleFresnelColor,
      gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity, gp.obstacleOutlineColor, gp.obstacleOutlineEmission]);

  const entries = useMemo(() => crouchWallEntries(crouches, crouchObstacles), [crouches, crouchObstacles]);
  // Halos for the crouches on the grid (SRVR-0314), in the wall fill colour.
  const haloMat = useMemo(() => makeHaloMaterial({ billboard: false, color: haloColor(gp.obstacleFillColor) }), [gp.obstacleFillColor]);
  useEffect(() => () => haloMat.dispose(), [haloMat]);
  const halos = useMemo(() => (geo ? new HaloPool(wallHaloGeometry(geo), haloMat, 'halo-walls-crouch', false, 'wall') : null), [geo, haloMat]);
  useEffect(() => () => halos?.dispose(), [halos]);
  const rims = useMemo(
    () => (geo ? new RimPool(geo, gp.obstacleScale * ((cfg.scaleX ?? 1) + (cfg.scaleY ?? 1)) / 2, 'selected-rim-crouch') : null),
    [geo, gp.obstacleScale, cfg.scaleX, cfg.scaleY],
  );
  useEffect(() => () => rims?.dispose(), [rims]);

  const normalRef = useRef<THREE.InstancedMesh | null>(null);
  const dimRef = useRef<THREE.InstancedMesh | null>(null);
  // Per-crouch grid-pop state, keyed by the crouch's time (how the format identifies it).
  const pop = useRef(new Map<number, ObsPopState>());
  // instanceId -> the crouch's time, per pool: a crouch is identified by its time (objectDrag.ts).
  const slotTimes = useRef({ normal: new Float64Array(OBS_POOL_LIMIT), dim: new Float64Array(OBS_POOL_LIMIT) });

  useEffect(() => {
    normalRef.current?.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    dimRef.current?.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  }, [geo, mats]);

  useFrame(() => {
    const normal = normalRef.current;
    const dimMesh = dimRef.current;
    if (!normal || !dimMesh) return;
    const rawBeat = chartBeat();
    const behind = Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats);
    const msPerBeat = 60000 / (bpm || 120);
    const minMs = (rawBeat - behind) * msPerBeat;
    const maxMs = (rawBeat + PERF.obstacleViewBeats) * msPerBeat;
    let lo = 0, hi = entries.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (entries[m].timeMs < minMs) lo = m + 1; else hi = m; }

    const store = useEditorStore.getState();
    const gridTol = onBeatTolerance(store.activeView, store.scrollStep, store.beatmap?.bpm);
    // Synth VR: only a wall exactly on the beat pops; one the playhead jumps over does not.
    const popCrossing = !isSynthVrView(store.activeView);
    const bs = gp.obstacleScale;
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const playing = store.playback.playing;
    if (pop.current.size > 1024) pop.current.clear();   // keep the map from growing over a session
    let nN = 0, nD = 0;
    halos?.begin();
    rims?.begin();
    const selected = new Set(store.selectedCrouches);
    // Far → near, so the transparent instances blend back to front (see InstancedObstaclePools).
    let end = lo;
    while (end < entries.length && entries[end].timeMs <= maxMs) end++;
    for (let i = end - 1; i >= lo; i--) {
      const { timeMs, position } = entries[i];
      const beat = timeMs / msPerBeat;
      const dim = beat < rawBeat && Math.abs(beat - rawBeat) >= gridTol;
      const mesh = dim ? dimMesh : normal;
      const slot = dim ? nD : nN;
      if (slot >= OBS_POOL_LIMIT) continue;
      const pulse = obstaclePop(pop.current, timeMs, beat - rawBeat, gridTol, nowMs, playing, popCrossing);
      const [wx, wy] = wallDrawXY(position, cfg);
      _oPos.set(wx, NOTE_Y_OFFSET + wy, -beat * BEAT_SPACING);
      // A crouch carries no roll of its own (`Charting.cs` `Crouch.ZRotation` is always 0).
      _oEuler.set(0, 0, cfg.rotation * DEG2RAD);
      _oQuat.setFromEuler(_oEuler);
      _oScale.set(bs * pulse * (cfg.scaleX ?? 1), bs * pulse * (cfg.scaleY ?? 1), bs * pulse * (cfg.scaleZ ?? 1));
      _oMatrix.compose(_oPos, _oQuat, _oScale);
      mesh.setMatrixAt(slot, _oMatrix);
      slotTimes.current[dim ? 'dim' : 'normal'][slot] = timeMs;
      if (dim) nD++; else nN++;
      if (haloOnGridNow(store, beat, rawBeat)) halos?.add(_oMatrix);
      if (selected.has(timeMs)) rims?.add(_oMatrix);
    }
    halos?.end();
    rims?.end();
    normal.count = nN;
    dimMesh.count = nD;
    normal.instanceMatrix.needsUpdate = true;
    dimMesh.instanceMatrix.needsUpdate = true;
    dropPickBounds(normal);
    dropPickBounds(dimMesh);
  });

  const dragFrom = (pool: 'normal' | 'dim') => (e: ThreeEvent<PointerEvent>) => {
    const id = (e as { instanceId?: number }).instanceId;
    const mesh = pool === 'dim' ? dimRef.current : normalRef.current;
    if (id == null || !mesh || id >= mesh.count) return;
    const timeMs = slotTimes.current[pool][id];
    if (e.button === 2 && isVr()) {   // a right press stops on the first object hit
      e.stopPropagation();
      if (isCtrlRightPress(e)) useEditorStore.getState().removeCrouch(timeMs);   // Remote Delete
      else if (isAltRightPress(e)) jumpPlayheadToObject(timeMs / (60000 / (bpm || 120)));   // go to its beat
      return;
    }
    startObjectDrag(e, { kind: 'crouch', timeMs });
  };

  if (!geo) return null;
  return (
    <>
      <instancedMesh key={`n-${shape}`} name="crouch-walls" ref={normalRef} args={[geo, mats.normal, OBS_POOL_LIMIT]} frustumCulled={false} onPointerDown={dragFrom('normal')} />
      <instancedMesh key={`d-${shape}`} name="crouch-walls-dim" ref={dimRef} args={[geo, mats.dim, OBS_POOL_LIMIT]} frustumCulled={false} raycast={dimPoolRaycast} onPointerDown={dragFrom('dim')} />
      {halos && <primitive key={`h-${shape}`} object={halos.mesh} />}
      {rims && <primitive key={`r-${shape}`} object={rims.mesh} />}
    </>
  );
}

// ── Main ObstacleModel Component ─────────────────────────────────────────────

export default function ObstacleModel({
  slide,
  index,
  selected = false,
}: {
  slide: Slide;
  /** Index into the current difficulty's slides array (for right-click delete). */
  index?: number;
  /** True when this obstacle is box-/select-all'd (draws the white rim). */
  selected?: boolean;
}) {
  const groupRef = useRef<THREE.Group>(null!);
  const configs = useEditorStore(s => s.gameplaySettings.obstacleConfigs);
  // Mesh refs collected by ObstacleFBX (stable array identity across renders).
  const meshesRef = useRef<THREE.Mesh[]>([]);
  // This obstacle's shape material pair, filled by ObstacleFBX (child renders
  // before this parent's effect runs, so it's set by registration time).
  const matsRef = useRef<ObstacleMats | null>(null);
  const haloRef = useRef<THREE.Mesh | null>(null);

  // Register with the central ticker (ONE loop updates all obstacles).
  useEffect(() => {
    const entry: ObstacleEntry = {
      beats: slide.time_beats,
      group: groupRef.current,
      meshes: meshesRef.current,
      matsRef,
      appliedMats: null,
      pulseStart: 0,
      prevOnGrid: false,
      dimmed: false,
      halo: haloRef,
    };
    _obstacleReg.add(entry);
    return () => { _obstacleReg.delete(entry); };
  }, [slide.time_beats]);

  // The context menu event only has its default prevented: right-button actions run on the press
  // (handlePointerDown), so one click never acts on two objects.
  const handleContextMenu = (e: import('@react-three/fiber').ThreeEvent<MouseEvent>) => {
    if (isVr()) { e.nativeEvent.preventDefault(); return; }
    // Other views: right-click on an obstacle deletes it, as it always did there.
    if (index == null) return;
    e.stopPropagation();
    e.nativeEvent.preventDefault();
    useEditorStore.getState().removeSlide(index);
  };

  // Left-click in Select mode: select this obstacle (Shift = add/toggle) so it
  // can be deleted together with notes. In other tool modes, don't hijack it.
  const handleClick = (e: import('@react-three/fiber').ThreeEvent<MouseEvent>) => {
    if (index == null) return;
    const s = useEditorStore.getState();
    if (!selectToolAvailable(s.activeView) || s.toolMode !== 'select') return;
    e.stopPropagation();
    const cur = s.selectedSlideIds;
    if (e.nativeEvent.shiftKey) {
      s.selectSlides(cur.includes(index) ? cur.filter(i => i !== index) : [...cur, index]);
    } else {
      s.selectNotes([]);
      s.selectSlides([index]);
    }
  };

  // Alt+press: drag the wall anywhere (objectDrag.ts), whatever the tool.
  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (index == null) return;
    // A right press is Alt = go to the wall's beat, Ctrl = Remote Delete; it stops on the first
    // object hit either way.
    if (e.button === 2 && isVr()) {
      e.stopPropagation();
      if (isAltRightPress(e)) jumpPlayheadToObject(slide.time_beats);
      else if (isCtrlRightPress(e)) useEditorStore.getState().removeSlide(index);
      return;
    }
    startObjectDrag(e, { kind: 'slide', index });
  };

  // Other views draw a wireframe box on a selected wall; Synth VR draws the white rim instead.
  const highlight = selected && !isVr() ? (
    <mesh renderOrder={999}>
      <boxGeometry args={[1.9, 2.7, 0.5]} />
      <meshBasicMaterial color="#FFD54A" wireframe transparent opacity={0.9} depthTest={false} fog={false} />
    </mesh>
  ) : null;

  const wt = slide.wall_type;

  // Get per-obstacle position from config, or fallback to 0,0
  const cfg = CONFIGURABLE_KEYS.has(wt)
    ? configs[wt as ObstacleConfigKey]
    : null;

  // The file position plus the type's offset — the same point the instanced pool draws at.
  const [wallX, wallY] = wallDrawXY(slide.position, cfg);
  const xPos = wallX;
  const yPos = NOTE_Y_OFFSET + wallY;

  // Per-obstacle roll from the .synth (zRotation) — matches the instanced pool so
  // a selected wall keeps its authored orientation. ObstacleFBX adds the wall
  // type's own cfg.rotation on top (both around Z, so they simply sum).
  const zRot = ((slide.z_rotation ?? 0) * Math.PI) / 180;

  // Check if we have an FBX model for this type
  const hasModel = CONFIGURABLE_KEYS.has(wt);
  if (!hasModel) {
    const wallColor = WALL_COLORS[wt] || '#ff444488';
    return (
      <group ref={groupRef} position={[xPos, yPos, 0]} rotation={[0, 0, zRot]} onContextMenu={handleContextMenu} onClick={handleClick} onPointerDown={handlePointerDown}>
        <FallbackSlideMarker color={wallColor} />
        {highlight}
      </group>
    );
  }

  return (
    <group ref={groupRef} position={[xPos, yPos, 0]} rotation={[0, 0, zRot]} onContextMenu={handleContextMenu} onClick={handleClick} onPointerDown={handlePointerDown}>
      <ObstacleFBX wallType={wt} meshesOut={meshesRef.current} matsRef={matsRef} haloRef={haloRef} selected={!!selected && isVr()} />
      {highlight}
    </group>
  );
}
